import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import { IconShieldLock, IconTruck } from '@tabler/icons-react';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import { useTouring } from 'components/self-tour/hooks/useTouring';

import { FreeCancellationIcon } from './FreeCancellationIcon';
import { BuyDirectContextModal } from './PropertySection/BuyDirectContextModal';
import { BuyBoxStats } from './PropertySection/CertaintyPanel/BuyBoxStats';
import type { BuyBoxCertaintyStats } from './PropertySection/CertaintyPanel/BuyBoxStats';
import { SelfTourAppHandoffModal } from './PropertySection/SelfTourAppHandoffModal';
import { TourWithAgentModal } from './PropertySection/TourWithAgentModal';

type TourAction = NonNullable<ReturnType<typeof useTouring>>['primaryAction'];
type BuyDirectCTAProperty = Pick<
  MarketplaceProperty,
  | 'addressToken'
  | 'buyDirectPrice'
  | 'buyDirectSavings'
  | 'canSelfTour'
  | 'displayBadge'
  | 'hasCurrentAvailability'
  | 'listingState'
  | 'listingTags'
  | 'slug'
>;

// atoms Button splits link vs button by `href`, so an action with an href (and
// not disabled) renders as an anchor; everything else renders as a button.
const renderTourAction = (action: TourAction) => (
  <Button
    analyticsName={action.analyticsName}
    data-analytics-name={action.analyticsName}
    aria-label={action.text}
    variant={action.variant}
    size="lg"
    onClick={action.onClick}
    disabled={action.disabled}
    href={action.disabled ? undefined : action.href}
    target={action.target}
    rel="noopener"
    className="w-full"
  >
    {action.text}
  </Button>
);

export const BuyDirectCTA = ({
  property,
  certaintyStats = null,
  hideTrustBadges = false,
}: {
  property: BuyDirectCTAProperty;
  // Certainty-panel treatment only; null renders the unchanged control card.
  certaintyStats?: BuyBoxCertaintyStats | null;
  // Set only when GuaranteeCard renders the same three protections below the
  // card; hiding the row without that replacement would drop them entirely.
  hideTrustBadges?: boolean;
}) => {
  const touringInfo = useTouring(property, { placement: 'main' });

  if (!touringInfo) {
    return null;
  }

  const {
    primaryAction,
    secondaryAction,
    tourKind,
    showBuyDirect,
    isDialogOpen,
    closeDialog,
    isSelfTourDialogOpen,
    closeSelfTourDialog,
    isBuyDirectContextOpen,
    closeBuyDirectContext,
    buyDirectContextHref,
  } = touringInfo;

  if (!showBuyDirect || property.buyDirectPrice == null) {
    const isTourWithAgent = tourKind === 'tourWithAgent';
    return (
      <>
        <Col className="gap-7 rounded-2xl border border-od-border-primary bg-od-bg-primary px-5 py-6 shadow-[0px_10px_22px_0px_rgba(0,0,0,0.04),0px_2px_12px_0px_rgba(0,0,0,0.04)]">
          <Col className="gap-2">
            <Text as="p" className="text-subheader-xs text-od-content-primary">
              Take a tour
            </Text>
            <Text as="p" className="text-body-md text-od-content-secondary">
              {isTourWithAgent
                ? 'Share this home with your agent and have them arrange a time to tour the home.'
                : 'Unlock the door with the Opendoor app yourself, no agent needed.'}
            </Text>
          </Col>
          <Col className="items-center gap-3">{renderTourAction(primaryAction)}</Col>
        </Col>
        {isTourWithAgent && (
          <TourWithAgentModal property={property} isOpen={isDialogOpen} onClose={closeDialog} />
        )}
        <SelfTourAppHandoffModal
          isOpen={isSelfTourDialogOpen}
          onClose={closeSelfTourDialog}
          placement="main"
          addressToken={property.addressToken}
          slug={property.slug}
        />
      </>
    );
  }

  const discount = property.buyDirectSavings ?? 0;
  const showDiscount = discount > 0;

  return (
    <>
      <Col className="gap-6 rounded-2xl border border-od-border-primary bg-od-bg-primary px-6 py-6 shadow-[0px_10px_22px_0px_rgba(0,0,0,0.04),0px_2px_12px_0px_rgba(0,0,0,0.04)]">
        <Col className="gap-3">
          {certaintyStats ? (
            <Row className="flex-wrap items-center gap-3">
              <Text as="p" className="text-label-lg text-od-content-primary">
                Buy with Opendoor
              </Text>
              {showDiscount && (
                <Box className="rounded-md bg-[color-mix(in_srgb,var(--color-od-bg-success)_10%,white)] px-2.5 py-0.5">
                  <Text as="span" className="text-body-sm font-medium text-od-content-success">
                    {formatCurrency(discount)} below list
                  </Text>
                </Box>
              )}
            </Row>
          ) : (
            <Text as="p" className="text-label-lg text-od-content-primary">
              Buy with Opendoor
            </Text>
          )}
          <Row className="items-center gap-4">
            <Text as="h3" className="text-header-sm text-od-content-primary">
              {formatCurrency(property.buyDirectPrice)}
            </Text>
          </Row>
          {!certaintyStats && showDiscount && (
            <Box className="self-start rounded-md bg-[color-mix(in_srgb,var(--color-od-bg-success)_10%,white)] px-2.5 py-0.5">
              <Text as="span" className="text-body-sm font-medium text-od-content-success">
                {formatCurrency(discount)} below list
              </Text>
            </Box>
          )}
          {certaintyStats && <BuyBoxStats stats={certaintyStats} />}
        </Col>

        <Row className={hideTrustBadges ? 'hidden' : 'hidden justify-between gap-4 lg:flex'}>
          <Row className="items-center gap-2">
            <IconTruck size={20} color="var(--color-od-content-primary)" />
            <Text as="p" className="text-body-sm text-od-content-primary">
              Early Move-In
            </Text>
          </Row>
          <Row className="items-center gap-2">
            <IconShieldLock size={20} color="var(--color-od-content-primary)" />
            <Text as="p" className="text-body-sm text-od-content-primary">
              100-Day Warranty
            </Text>
          </Row>
          <Row className="items-center gap-2">
            <FreeCancellationIcon size={20} className="text-od-content-primary" />
            <Text as="p" className="text-body-sm text-od-content-primary">
              Free Cancellation
            </Text>
          </Row>
        </Row>

        <Col className="items-center gap-3">
          <Col className="w-full gap-4">
            {secondaryAction && renderTourAction(secondaryAction)}
            {renderTourAction(primaryAction)}
          </Col>
          {certaintyStats && (
            <Text as="p" className="text-body-sm text-od-content-tertiary">
              No commitment, review before signing
            </Text>
          )}
        </Col>
      </Col>
      {tourKind === 'tourWithAgent' && (
        <TourWithAgentModal property={property} isOpen={isDialogOpen} onClose={closeDialog} />
      )}
      <SelfTourAppHandoffModal
        isOpen={isSelfTourDialogOpen}
        onClose={closeSelfTourDialog}
        placement="main"
        addressToken={property.addressToken}
        slug={property.slug}
      />
      <BuyDirectContextModal
        isOpen={isBuyDirectContextOpen}
        onClose={closeBuyDirectContext}
        continueHref={buyDirectContextHref}
      />
    </>
  );
};
